import { StateCreator } from "zustand";
import { Database, supabase } from "../../../../lib/supabase";
import { SupabaseWriteTableEnum } from "../../../../lib/supabase/supabaseTypes";
import { Logger } from "../../../../lib/logger/Logger";
import { showNotification } from "./selectors";
import { debounce } from "lodash";
import { produce } from "immer";
import { v4 as uuidv4 } from "uuid";
import { StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { fetchClientAvatar } from "./utils";

type ClientFieldValues = Database["public"]["Tables"]["clients"]["Row"];

// Text fields that need debounced saving
const CLIENT_FORM_TEXT_FIELDS = [
	// Rechnungen fields
	"discount_work",
	"discount_material",
	"monthly_discount",
	"default_tp_variation",
	// Allgemeine Informationen fields
	"code",
	"company_name",
	"first_name",
	"last_name",
	"title",
	"street",
	"postal_code",
	"city",
	"country",
	"additional_information",
	"phone_personal",
	"phone_business",
	"phone_mobile",
	"email",
	"fax",
	"external_reference",
	"note",
	"eori_number",
	// Correspondence address fields
	"c_company_name",
	"c_first_name",
	"c_last_name",
	"c_title",
	"c_street",
	"c_postal_code",
	"c_city",
	"c_country",
	"c_additional_information",
	"c_phone_personal",
	"c_phone_business",
	"c_phone_mobile",
	"c_email",
	"c_fax",
] as const;

// Properties that trigger warning notifications when changed
const CLIENT_PROPERTIES_TO_WARN = [
	"discount_material",
	"discount_work",
	"default_tp_value",
	"default_tp_tier",
	"default_tp_variation",
	"default_activated_region_id",
] as const;

export interface ClientFormsSlice {
	// State for text fields that need debounced saving
	clientFormTextFields: Record<string, string>;

	// State for avatar upload
	pendingAvatarFile: File | null;

	// Actions
	initializeClientFormFields: (client: ClientFieldValues | null) => void;
	updateClientFormTextField: (field: string, value: string) => void;
	saveClientTextFields: () => Promise<void>;
	checkAndWarnForClientChanges: (field: string, newValue: any) => void;

	updateClientField: {
		<K extends keyof ClientFieldValues>(
			field: K,
			value: ClientFieldValues[K]
		): Promise<void>;
		(fields: Partial<ClientFieldValues>): Promise<void>;
	};

	// Helper for immediate updates with warning check
	handleImmediateClientFieldUpdate: (
		field: string,
		value: any
	) => Promise<void>;

	// Avatar handling
	setPendingAvatarFile: (file: File | null) => void;
	uploadClientAvatar: () => Promise<void>;

	// Cleanup
	resetClientFormFields: () => void;
}

export const createClientFormsStore: StateCreator<
	any,
	[],
	[],
	ClientFormsSlice
> = (set, get) => {
	// Debounced save function for text fields
	const debouncedSaveTextFields = debounce(async () => {
		const state = get();
		await state.saveClientTextFields();
	}, 700);

	return {
		// Initial state
		clientFormTextFields: {},
		pendingAvatarFile: null,

		// Initialize text fields from client object
		initializeClientFormFields: (client: ClientFieldValues | null) => {
			if (!client) {
				// Reset form fields when no client
				set({ clientFormTextFields: {} });
				return;
			}

			const initialValues: Record<string, string> = {};
			CLIENT_FORM_TEXT_FIELDS.forEach((field) => {
				const value = client[field as keyof typeof client];
				initialValues[field] = value?.toString() || "";
			});

			set({ clientFormTextFields: initialValues });
		},

		// Update a text field value and trigger debounced save
		updateClientFormTextField: (field: string, value: string) => {
			set(
				produce((state: any) => {
					state.clientFormTextFields[field] = value;
				})
			);

			// Trigger debounced save
			debouncedSaveTextFields();
		},

		// Save all text fields that have changed
		saveClientTextFields: async () => {
			const state = get();
			const client = state.client;
			const textFields = state.clientFormTextFields;

			if (!client) return;

			const updates: any = {};
			let hasChanges = false;

			// Check each text field for changes
			Object.keys(textFields).forEach((field) => {
				const currentValue = textFields[field];
				const originalValue =
					client[field as keyof typeof client]?.toString() || "";

				if (currentValue !== originalValue) {
					// Convert to appropriate type
					if (
						field.includes("discount") ||
						field.includes("variation")
					) {
						updates[field] = currentValue
							? Number(currentValue)
							: null;
					} else {
						updates[field] = currentValue || null;
					}
					hasChanges = true;
				}
			});

			if (hasChanges) {
				// Check for changes that need warning
				Object.keys(updates).forEach((key) => {
					state.checkAndWarnForClientChanges(key, updates[key]);
				});

				await state.updateClientField(updates);
			}
		},

		// Check if a field change requires a warning notification
		checkAndWarnForClientChanges: (field: string, newValue: any) => {
			const state = get();
			const client = state.client;

			if (!client) return;

			if (CLIENT_PROPERTIES_TO_WARN.includes(field as any)) {
				const oldValue = client[field as keyof typeof client];
				if (oldValue !== newValue) {
					showNotification({
						message:
							"Änderungen an Rabatten und Tarifen auf Auftragsebene werden bei bereits erstellten Aufträgen nicht automatisch angepasst!",
						type: "info",
					});
				}
			}
		},

		// Moved from client.store.ts - handles both single and multiple field updates
		updateClientField: async (fieldOrFields: any, value?: any) => {
			const state = get();
			const client = state.client;
			const clientId = state.clientId;

			if (!client || !clientId) {
				Logger.error("No client selected when updating client field");
				return;
			}

			// Determine if single field or multiple fields
			const updates: Partial<ClientFieldValues> =
				typeof fieldOrFields === "string"
					? { [fieldOrFields]: value }
					: fieldOrFields;

			// Update local state optimistically
			const updatedClient = { ...client, ...updates };
			set(
				produce((state: any) => {
					state.clientsLookup[clientId] = updatedClient;
					state.client = updatedClient;
				})
			);

			// Update database
			const { error } = await supabase
				.from(SupabaseWriteTableEnum.CLIENTS)
				.update(updates)
				.eq("id", clientId);

			if (error) {
				Logger.error(
					"Failed to update client fields",
					{},
					error.message
				);
				showNotification({
					message: "Kunde konnte nicht aktualisiert werden",
					type: "error",
				});

				// Revert local state on error
				set(
					produce((state: any) => {
						state.clientsLookup[clientId] = client;
						state.client = client;
					})
				);
			}
		},

		// Helper for immediate updates with warning check
		handleImmediateClientFieldUpdate: async (field: string, value: any) => {
			const state = get();
			state.checkAndWarnForClientChanges(field, value);
			await state.updateClientField(field, value);
		},

		// Avatar handling
		setPendingAvatarFile: (file: File | null) => {
			set({ pendingAvatarFile: file });
		},

		uploadClientAvatar: async () => {
			const state = get();
			const { pendingAvatarFile, client, organization } = state;

			if (!pendingAvatarFile || !client || !organization) {
				return;
			}

			const avatarPath = `avatars/${uuidv4()}/avatar`;

			try {
				// Upload file to Supabase storage
				const { error: uploadError } = await supabase.storage
					.from(StorageBucketsEnum.ORG_V1)
					.upload(
						`${organization.id}/${avatarPath}`,
						pendingAvatarFile
					);

				if (uploadError) {
					showNotification({
						message: "Fehler beim Hochladen des Avatars",
						type: "error",
					});
					Logger.error(
						"Avatar upload failed",
						{},
						uploadError.message
					);
					return;
				}

				// Fetch the signed URL for the new avatar
				const image_src = await fetchClientAvatar(avatarPath);

				// Update the avatar_path in the database
				await state.updateClientField("avatar_path", avatarPath);

				// Update image_src in local state only (not a database field)
				const clientId = state.clientId;
				if (clientId) {
					set(
						produce((state: any) => {
							if (state.client) {
								state.client.image_src = image_src;
							}
							if (state.clientsLookup[clientId]) {
								state.clientsLookup[clientId].image_src =
									image_src;
							}
						})
					);
				}

				// Clear pending file
				set({ pendingAvatarFile: null });

				showNotification({
					message: "Avatar erfolgreich aktualisiert",
					type: "success",
				});
			} catch (error) {
				Logger.error("Avatar upload failed", {}, error);
				showNotification({
					message: "Fehler beim Hochladen des Avatars",
					type: "error",
				});
			}
		},

		// Reset form fields
		resetClientFormFields: () => {
			set({
				clientFormTextFields: {},
				pendingAvatarFile: null,
			});
		},
	};
};
